import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { CustomerAccountSummary, ManagedByReseller } from '../types/clientAccount';
import customerApiService from '../services/customerApi';
import { useCustomerAuth } from './CustomerAuthContext';

// Estado compartido del portal cliente que necesitan navbar/layout/guards:
// GET /customer/account trae managed_by_reseller {name, contact_email} | null.
// Si la cuenta la gestiona un partner, TODO lo de billing/pagos/planes Paddle
// se oculta (menú y páginas) y se enseña el banner de cuenta gestionada.
// Tolerante: sin cuenta (404) o backend sin el campo → null y el portal se
// comporta como siempre.

interface CustomerPortalContextType {
  account: CustomerAccountSummary | null;
  managedByReseller: ManagedByReseller | null;
  // true mientras no sabemos aún si la cuenta es gestionada (evita parpadeos
  // y redirecciones prematuras en los guards).
  loading: boolean;
  refresh: () => Promise<void>;
}

const CustomerPortalContext = createContext<CustomerPortalContextType | undefined>(undefined);

export const CustomerPortalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading: authLoading, mustChangePassword } = useCustomerAuth();
  const [account, setAccount] = useState<CustomerAccountSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await customerApiService.getMyAccount();
      setAccount(data);
    } catch (err) {
      // Nunca romper el portal por esto: sin dato = comportamiento clásico.
      console.error('Failed to load customer account (portal context):', err);
      setAccount(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setAccount(null);
      setLoading(false);
      return;
    }
    // Con el gate de contraseña levantado el backend VETA esta llamada (403
    // PASSWORD_CHANGE_REQUIRED): no se pide. `mustChangePassword` está en las
    // dependencias precisamente para que al salir del gate (true→false) la
    // cuenta se cargue — si no, el portal se quedaría sin `managed_by_reseller`
    // hasta la siguiente recarga y enseñaría menús de billing que no le tocan.
    if (mustChangePassword) {
      setAccount(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    refresh();
  }, [isAuthenticated, authLoading, mustChangePassword, refresh]);

  return (
    <CustomerPortalContext.Provider
      value={{
        account,
        managedByReseller: account?.managed_by_reseller ?? null,
        loading: authLoading || loading,
        refresh,
      }}
    >
      {children}
    </CustomerPortalContext.Provider>
  );
};

export const useCustomerPortal = () => {
  const context = useContext(CustomerPortalContext);
  if (context === undefined) {
    throw new Error('useCustomerPortal must be used within a CustomerPortalProvider');
  }
  return context;
};
